import { useEffect, useState } from "react";
import classNames from "classnames";
import * as RadixToast from "@radix-ui/react-toast";
// eslint-disable-next-line css-modules/no-unused-class
import styles from "./toast.module.css";
import { datadogLogger } from "lib/datadog/logger";

type ToastProps = {
  message: string;
  color?:
    | "black"
    | "green"
    | "red"
    | "brand"
    | "brand-sifted"
    | "brand-pro"
    | "pro"
    | "sifted"
    | "cerise"
    | "deep-blue"
    | "pro-blue"
    | "black-rock"
    | "secondary-cta"
    | "negative"
    | "positive"
    | "upround"
    | "climate"
    | "startuplife"
    | "fintech"
    | "deeptech"
    | "mono-70"
    | "mono-40"
    | "mono-30";
  children?: React.ReactNode;
  isOpen?: boolean;
  hide?: boolean;
  variant?: "default" | "copied";
};

const Toast = ({
  message,
  color = "black",
  children,
  isOpen,
  hide,
  variant = "default",
}: ToastProps) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setOpen(true);
    } else if (typeof isOpen === "boolean" && isOpen === false) {
      setOpen(false);
    }
  }, [isOpen]);

  if (message && message.length > 75 && variant === "default") {
    datadogLogger.warn(
      "Please ensure toast message does not exceed three lines, see style system for details"
    );
  }

  if (hide) return null;
  return (
    <div>
      <RadixToast.Provider duration={2700}>
        <div
          data-testid="toast-trigger"
          role="button"
          onClick={() => setOpen(true)}
        >
          {children}
        </div>
        <RadixToast.Root
          className={classNames(styles.ToastRoot, {
            [styles.copied]: variant === "copied",
          })}
          open={open}
          onOpenChange={setOpen}
        >
          <RadixToast.Title
            className={classNames(styles.ToastTitle, {
              "leading-xl": variant === "default",
              // Legacy color mappings
              "text-negative":
                variant === "default" &&
                (color === "red" || color === "negative"),
              "text-positive":
                variant === "default" &&
                (color === "green" || color === "positive"),
              "text-mono-70":
                variant === "default" &&
                (color === "black" || color === "mono-70"),
              // Brand colors
              "text-brand": variant === "default" && color === "brand",
              "text-brand-sifted":
                variant === "default" && color === "brand-sifted",
              "text-brand-pro": variant === "default" && color === "brand-pro",
              // Main brand colors
              "text-pro": variant === "default" && color === "pro",
              "text-sifted": variant === "default" && color === "sifted",
              "text-cerise": variant === "default" && color === "cerise",
              "text-deep-blue": variant === "default" && color === "deep-blue",
              "text-pro-blue": variant === "default" && color === "pro-blue",
              "text-black-rock":
                variant === "default" && color === "black-rock",
              // Utility colors
              "text-secondary-cta":
                variant === "default" && color === "secondary-cta",
              // Category colors
              "text-upround": variant === "default" && color === "upround",
              "text-climate": variant === "default" && color === "climate",
              "text-startuplife":
                variant === "default" && color === "startuplife",
              "text-fintech": variant === "default" && color === "fintech",
              "text-deeptech": variant === "default" && color === "deeptech",
              "text-mono-40": variant === "default" && color === "mono-40",
              "text-mono-30": variant === "default" && color === "mono-30",
              // Copied Variant
              "font-regular text-sm leading-tight text-brand-neutral":
                variant === "copied",
            })}
          >
            {message}
          </RadixToast.Title>

          {variant === "default" && (
            <RadixToast.Action
              data-testid="toast-close"
              className={styles.ToastAction}
              asChild
              altText="Close"
            >
              <button onClick={() => setOpen(false)}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-mono-70"
                >
                  <path d="M3 3L21 21" stroke="currentColor" strokeWidth="2" />
                  <path d="M3 21L21 3" stroke="currentColor" strokeWidth="2" />
                </svg>
              </button>
            </RadixToast.Action>
          )}
        </RadixToast.Root>
        <RadixToast.Viewport className={styles.ToastViewport} />
      </RadixToast.Provider>
    </div>
  );
};

export default Toast;
